import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Mail, MessageCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { CTAButton } from "../components/CTAButton";
import { Reveal } from "../components/Reveal";
import { SITE, whatsappLink } from "../data/config";
import { submitLead } from "../lib/submit";

const inputCls =
  "w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-signal transition-colors";

export default function Contact() {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ name: "", email: "", whatsapp: "", message: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name) return toast.error(t("contact.errName"));
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return toast.error(t("contact.errEmail"));
    if (!f.message) return toast.error(t("contact.errMessage"));
    const res = await submitLead("leads", { source: "r0am_website", lead_type: "contact", ...f });
    if (!res.ok) return toast.error(t("contact.errMessage"));
    setDone(true);
    toast.success(t("contact.sentToast"));
  };

  return (
    <>
      <PageHero overline={t("contact.overline")} title={t("contact.title")} subtitle={t("contact.subtitle")} accent="signal" testId="contact-hero" />

      <section className="py-16">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="surface p-8">
                <h2 className="font-display font-semibold text-xl">{t("contact.reachUs")}</h2>
                <a href={`mailto:${SITE.email}`} className="mt-6 flex items-center gap-3 text-white/70 hover:text-white" data-testid="contact-email">
                  <span className="w-10 h-10 rounded-lg bg-signal/10 text-signal grid place-items-center"><Mail size={18} /></span>
                  {SITE.email}
                </a>
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="mt-4 flex items-center gap-3 text-white/70 hover:text-white" data-testid="contact-whatsapp">
                  <span className="w-10 h-10 rounded-lg bg-health/10 text-health grid place-items-center"><MessageCircle size={18} /></span>
                  {t("contact.whatsappSupport")}
                </a>
                <p className="mt-8 text-xs text-white/40">{t("contact.note", { pronounced: t("common.pronounced") })}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="surface p-10 text-center" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full bg-health/15 grid place-items-center mx-auto"><CheckCircle2 size={28} className="text-health" /></div>
                  <h3 className="mt-5 font-display font-bold text-2xl">{t("contact.successTitle")}</h3>
                  <p className="mt-2 text-white/60">{t("contact.successBody", { name: f.name.split(" ")[0] })}</p>
                  <p className="mt-4 text-xs text-white/35">{t("contact.demoNote")}</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="surface p-6 sm:p-9" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className="block overline text-white/40 mb-2">{t("contact.fName")}</label><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="c-name" /></div>
                    <div><label className="block overline text-white/40 mb-2">{t("contact.fEmail")}</label><input type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="c-email" /></div>
                  </div>
                  <div className="mt-4"><label className="block overline text-white/40 mb-2">{t("contact.fWhatsapp")}</label><input className={inputCls} value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+..." data-testid="c-whatsapp" /></div>
                  <div className="mt-4"><label className="block overline text-white/40 mb-2">{t("contact.fMessage")}</label><textarea rows="4" className={inputCls} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="c-message" /></div>
                  <div className="mt-6"><CTAButton type="submit" variant="primary" fullWidth testId="contact-submit">{t("common.sendMessage")} <ArrowRight size={16} className="rtl:rotate-180" /></CTAButton></div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
